import {AfterViewInit, Component, DoCheck, EventEmitter, Input, KeyValueDiffer, KeyValueDiffers, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {InputConfig} from "../input";
import {ReplaySubject, Subject} from "rxjs";
import {take, takeUntil} from "rxjs/operators";
import {HttpClient} from "@angular/common/http";
import {MatSelect, MatSelectChange} from "@angular/material/select";

export interface SelectSearchConfig extends InputConfig {

    attrs: {
        required?: boolean;
        label?: string;
        choices?: OptionsSelectConfig[];
        callbackApi?: string;
        callbackData?: object;
        multiple?: boolean;
        help?: string;
    };
}

export interface OptionsSelectConfig {

    id: number | string;

    name: string;

    choices?: any[];
}

@Component({
    selector: 'app-select-search',
    templateUrl: './select-search.component.html',
    styleUrls: ['./select-search.component.scss']
})
export class SelectSearchComponent implements OnInit, AfterViewInit, OnDestroy, DoCheck {

    @Input() public config: SelectSearchConfig = {id: null, attrs: {}};

    @Input() public form: UntypedFormGroup;

    @Input() public value: string;

    @Output() public valueChange: EventEmitter<MatSelectChange> = new EventEmitter();

    @Output() public keypressSearch: EventEmitter<any> = new EventEmitter();

    @Input() public disabled: boolean;

    @ViewChild('singleSelect', {static: true}) public singleSelect: MatSelect;

    private _differ: KeyValueDiffer<any, any>;

    private _onDestroy: Subject<void> = new Subject<void>();

    public valuesOptions: OptionsSelectConfig[];

    public selectCtrl: UntypedFormControl = new UntypedFormControl();

    public optionFilterCtrl: UntypedFormControl = new UntypedFormControl();

    public filteredOptions: ReplaySubject<object[]> = new ReplaySubject<object[]>(1);

    public constructor(
        private _http: HttpClient,
        private _differs: KeyValueDiffers
    ) {}

    ngOnInit(): void {

        this._differ = this._differs.find(this.config).create();

        if (!this.config.attrs.multiple) {

            this.config.attrs.multiple = false;
        }

        if (this.config.attrs && this.config.attrs.callbackApi) {

            this.config.attrs.choices = this.getData(this.config.attrs.callbackApi, this.config.attrs.callbackData);

            this.initValues();

        } else {

            this.initValues();
        }

        this.initForm();
    }

    ngDoCheck(): void {

        const changes: any = this._differ.diff(this.config.attrs);

        if (changes) {

            changes.forEachChangedItem((r: any) => {

                if (r.key === 'choices') {

                    this.config.attrs.choices = r.currentValue;

                    this.initValues();
                }

                if (r.key === 'callbackApi') {

                    this._updateFormValue('');

                    this.config.attrs.choices = this.getData(this.config.attrs.callbackApi, this.config.attrs.callbackData);

                    this.initValues();
                }
            });
        }
    }

    ngAfterViewInit(): void {

        this.setInitialValue();
    }

    ngOnDestroy(): void {

        this._onDestroy.next();

        this._onDestroy.complete();
    }

    private _updateFormValue(value: unknown): void {

        this.selectCtrl.patchValue(value);
    }

    public getData(apiUrl: string, apiData: object): any[] {

        const data: object[] = [];

        this._http.get<any>(`${apiUrl}`).subscribe((d: []) => {

            d['hydra:member'].forEach((elem: any) => {

                let newData: object = {};

                newData = {'id': '' + elem[apiData['key']], 'name': elem[apiData['value']]};

                data.push(newData);
            });

            this.initValues();
        });

        return data;
    }

    public initValues(): void {

        this.valuesOptions = this.config.attrs.choices;

        this.filteredOptions.next(this.valuesOptions.slice());

        this.optionFilterCtrl.valueChanges
            .pipe(takeUntil(this._onDestroy))
            .subscribe(() => {
                this.filterOptions();
            });

        this.filterOptions();
    }

    public initForm(): void {

        const validators: any[] = [];

        this.form.addControl(this.config.id, this.selectCtrl);

        if (('required' in this.config.attrs) && this.config.attrs.required) {

            validators.push(Validators.required);
        }

        this.form.controls[this.config.id].setValidators(validators);

        if(this.value){

            this._updateFormValue(this.value);
        }

        if(this.disabled){

            this.form.controls[this.config.id].disable();
        }
    }

    public setInitialValue(): void {

        this.filteredOptions
            .pipe(take(1), takeUntil(this._onDestroy))
            .subscribe(() => {
                if (this.singleSelect !== undefined) {
                    this.singleSelect.compareWith = (a: OptionsSelectConfig, b: OptionsSelectConfig): boolean => a && b && a.id === b.id;
                }
            });
    }

    public filterOptions(): void {

        if (!this.valuesOptions) {

            return;
        }

        let search: string = this.optionFilterCtrl.value;

        if (!search) {

            this.filteredOptions.next(this.valuesOptions);

            return;

        } else {

            search = search.toLowerCase();
        }

        this.filteredOptions.next(this.valuesOptions.filter((valuesOptions: any) => valuesOptions.name.toLowerCase().indexOf(search) > -1));
    }

    public selectChange(event: MatSelectChange): void {

        this.valueChange.emit(event);
    }
}
